import { Component, OnDestroy, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { CookieService } from 'ngx-cookie';
import { Subject } from 'rxjs';

import SwiperCore, { Autoplay, Pagination } from 'swiper';
import { setTheme } from 'ngx-bootstrap/utils';

import { environment } from 'src/environments/environment';
import { PopupConfig } from 'src/app/component/popup/popup.component';
import { AuthService } from 'src/app/service/auth.service';
import { CheckDesktopService } from 'src/app/service/check-desktop.service';
import { FontLoaderService } from 'src/app/service/font-loader.service';
import { MessageService } from 'src/app/service/message.service';
import { UserService } from 'src/app/service/user.service';
import { identifyUser, pushUserData } from './service/analytics.service';
import { Alliance } from './model/alliance';
import { User } from './model/user';
import { routeTransitionAnimations } from './route-transition-animations';
import { AllianceService } from './service/alliance.service';
import { ChannelService } from './service/channel.service';
import { LanguageService } from './service/language.service';

SwiperCore.use([Pagination, Autoplay]);
@Component({
  selector: 'root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  animations: [routeTransitionAnimations],
})
export class AppComponent implements OnInit, OnDestroy {
  user: User;
  isDesktop;
  popupConfig: PopupConfig;
  alliance: Alliance;
  private destroy$ = new Subject<void>();

  constructor(
    private cookieService: CookieService,
    private userService: UserService,
    private authService: AuthService,
    private channelService: ChannelService,
    private messageService: MessageService,
    private cds: CheckDesktopService,
    private allianceService: AllianceService,
    private fontLoaderService: FontLoaderService,
    private languageService: LanguageService,
  ) {
    // Set Bootstrap 5 theme for ngx-bootstrap
    setTheme('bs5');

    // Load web fonts asynchronously to prevent render-blocking
    this.fontLoaderService.loadFonts();
  }

  ngOnInit(): void {
    this.checkBrowser();
    this.authService.getCurrentUser().subscribe((user) => {
      this.user = user;
      this.cds.isDesktop.subscribe((resp) => (this.isDesktop = resp));
      this.messageService.event.subscribe((resp) => {
        this.popupConfig = resp;
      });
      this.setChannel();
      this.setAlliance(user);
      this.setDanalAuthCookie();
      this.setUserData();
      this.setMixpanel();
    });
  }

  setDanalAuthCookie() {
    const message = this.cookieService.get('nnb-danal-auth-fail');
    this.cookieService.remove('nnb-danal-auth-fail');
    if (message) {
      setTimeout(() => {
        this.messageService.popup({
          title: '본인인증 실패',
          description: message,
          cancelLabel: '확인',
          isNested: true,
          isStrict: true,
        });
      }, 1300);
    }
  }

  setAlliance(user: User) {
    const origin = window.location.origin;
    const isExistSubdomain = origin !== environment.webDomain;
    if (isExistSubdomain) {
      this.allianceService.get(origin).subscribe({
        next: async (alliance) => {
          this.alliance = alliance;
          this.allianceService.setAlliance(alliance);
          if (alliance && user && !user.company) {
            await this.userService.editAndUpdateToken(user.id, {
              company: alliance.name,
            });
          }
        },
        error: () => {},
      });
    }
  }

  prepareRoute(outlet: RouterOutlet) {
    return outlet?.activatedRouteData?.animationState;
  }

  checkBrowser() {
    const isIEOrEdge = /msie\s|trident\/|edge\//i.test(window.navigator.userAgent);
    if (isIEOrEdge) {
      this.messageService.popup({
        description:
          '노는법은 구글 크롬 브라우저에 최적화 되어 있습니다. 사용 중이신 브라우저에서는 오류가 발생할 수 있으니 크롬 브라우저에서 이용해주세요.',
        cancelLabel: '확인',
      });
    }
  }

  setChannel() {
    if (this.user) {
      this.channelService.boot({
        pluginKey: 'ba55a3cb-ad37-4bdc-b07a-0e9ef20aeb88',
        member: true,
        memberId: this.user.id,
        profile: {
          name: this.user.nickname,
          avatarUrl: this.user.profilePhoto,
          mobileNumber: this.user?.phoneNumber,
          email: this.user.email,
          role: this.user.role,
        },
      });
    } else {
      this.channelService.boot({
        pluginKey: 'ba55a3cb-ad37-4bdc-b07a-0e9ef20aeb88',
      });
    }
  }

  setUserData() {
    if (this.user) {
      pushUserData(this.user);
    }
  }

  setMixpanel() {
    if (this.user) {
      identifyUser(this.user);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
